/**
 * Public /register page — marketing-site entry point.
 *
 * Designed to be linked from lenavio.com with prefill query params so the
 * visitor arrives with the form half-filled. Supported params:
 *   ?email=…        — prefills "Work email"
 *   ?name=…         — parsed into first/last name
 *   ?company=…      — prefills "Company name" and seeds the slug
 *   ?size=…         — prefills the "Company size" select
 *   ?plan=…         — not used by the backend yet; preserved as a hidden
 *                     field for later attribution
 *
 * Differs from the pre-existing /signup page by:
 *   - explicit password confirm field
 *   - auto-generated + editable slug preview
 *   - terms / privacy links to the external marketing legal pages
 *
 * Both pages hit the same ``POST /api/v1/signup/org`` endpoint.
 */
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { Button, Input, Card } from "@/components/ui";
import { PasswordRequirements } from "@/components/auth/PasswordRequirements";
import { useAuth } from "@/hooks/useAuth";
import { setAccessToken } from "@/api/client";
import { signupOrg } from "@/api/signup";
import { LanguageSwitcher } from "@/components/layout/LanguageSwitcher";
import {
  GoogleAuthButton,
  OrEmailDivider,
} from "@/components/auth/GoogleAuthButton";

const TERMS_URL = "https://www.lenavio.com/legal/terms";
const PRIVACY_URL = "https://www.lenavio.com/legal/privacy";

const SIZE_OPTIONS = [
  { value: "", label: "Select…" },
  { value: "1-10", label: "1–10" },
  { value: "10-50", label: "10–50" },
  { value: "50-200", label: "50–200" },
  { value: "200+", label: "200+" },
];

/**
 * Marketing sites usually pass sizes as either our internal tokens ("1-10",
 * "10-50", …) or freer labels ("1-10 employees", "50–200"). Normalize both
 * onto the select's value space; anything unrecognizable falls through to
 * the placeholder option.
 */
function normalizeSize(raw: string | null): string {
  if (!raw) return "";
  const cleaned = raw.toLowerCase().replace(/[\s_]+/g, "").replace(/–/g, "-");
  for (const opt of SIZE_OPTIONS) {
    if (opt.value && cleaned.startsWith(opt.value.toLowerCase())) {
      return opt.value;
    }
  }
  return "";
}

/**
 * Turn a free-form company name into a URL-safe slug: lowercase ASCII,
 * dashes for spaces, non-alphanum stripped, dashes collapsed. The server
 * also normalizes on its side, but previewing it here lets the visitor
 * spot and fix a bad transliteration before submitting.
 */
function slugifyCompany(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
}

/**
 * Turn whatever shape the signup endpoint throws at us into one clean
 * sentence the visitor can act on. The backend can return three very
 * different error shapes:
 *
 *   - 400/409 with ``detail: "email already taken"``           (plain string)
 *   - 422 with ``detail: [{ loc, msg, type }, …]``             (pydantic)
 *   - Network error with no ``response`` at all                (fetch fail)
 *
 * Rather than let any of those leak as raw JSON, classify by the field
 * name that failed and map to copy the visitor understands.
 */
function humanizeSignupError(err: unknown): string {
  const generic = "We couldn't create your workspace. Please try again.";
  const response = (err as { response?: { data?: unknown; status?: number } })
    ?.response;
  if (!response) {
    return (
      (err as Error)?.message ||
      "Network error — please check your connection and try again."
    );
  }

  const detail = (response.data as { detail?: unknown })?.detail;

  // Case 1: pydantic validation error list.
  if (Array.isArray(detail)) {
    const items = detail as Array<{ loc?: unknown[]; msg?: string; type?: string }>;
    for (const item of items) {
      const field = String(item.loc?.[item.loc.length - 1] ?? "");
      if (field === "accept_terms" || field === "accept_privacy") {
        return "Please accept the Terms and Privacy Policy to continue.";
      }
      if (field === "admin_email") {
        return "Please enter a valid work email address.";
      }
      if (field === "password") {
        return item.msg || "That password doesn't meet our requirements.";
      }
      if (field === "preferred_slug" || field === "org_name") {
        return item.msg || "That workspace name isn't available.";
      }
    }
    // Fallback — first message in the list, without the pydantic prefix.
    const first = items[0];
    if (first?.msg) return first.msg.replace(/^value_error,?\s*/i, "");
    return generic;
  }

  // Case 2: plain string detail.
  if (typeof detail === "string") {
    const lower = detail.toLowerCase();
    if (lower.includes("accept_terms") || lower.includes("accept_privacy")) {
      return "Please accept the Terms and Privacy Policy to continue.";
    }
    if (lower.includes("email") && lower.includes("taken")) {
      return "An account with that email already exists. Try signing in instead.";
    }
    if (lower.includes("slug") && lower.includes("taken")) {
      return "That workspace URL is already in use. Try a different company name.";
    }
    return detail;
  }

  return generic;
}

function splitName(full: string): { first: string; last: string } {
  const parts = full.trim().split(/\s+/);
  if (parts.length === 0) return { first: "", last: "" };
  if (parts.length === 1) return { first: parts[0], last: "" };
  return { first: parts[0], last: parts.slice(1).join(" ") };
}

export function RegisterPage() {
  const { isAuthenticated } = useAuth();
  const [params] = useSearchParams();

  // ``provider=google`` lands here after the OAuth callback when the
  // visitor's Google email isn't yet a Lenavio user — Google has
  // already proven their identity, so we don't need (or want) them
  // to invent a password. The flow auto-generates a high-entropy
  // password under the hood (so the existing /signup/org endpoint
  // is happy), and the user keeps signing in via Google going
  // forward. They can reset to a real password later if they want.
  const googleSignup = (params.get("provider") || "").toLowerCase() === "google";

  const [orgName, setOrgName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugDirty, setSlugDirty] = useState(false);
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [companySize, setCompanySize] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Prefill from marketing-site query params on first render.
  useEffect(() => {
    const paramEmail = params.get("email")?.trim();
    const paramName = params.get("name")?.trim();
    const paramCompany = params.get("company")?.trim();
    const paramSize = normalizeSize(params.get("size"));

    if (paramEmail) setEmail(paramEmail);
    if (paramName) setFullName(paramName);
    if (paramCompany) {
      setOrgName(paramCompany);
      setSlug(slugifyCompany(paramCompany));
    }
    if (paramSize) setCompanySize(paramSize);
    // Only on mount — we don't want to clobber the user's typing if they
    // come back to the query string via history navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-track the slug while the user types a company name, unless
  // they've manually tweaked the slug field.
  useEffect(() => {
    if (!slugDirty) setSlug(slugifyCompany(orgName));
  }, [orgName, slugDirty]);

  const plan = useMemo(() => params.get("plan")?.trim() || null, [params]);

  // ``?skip_trial=true`` along with ``?plan=essentials|professional|enterprise``
  // turns this page into a direct-subscribe flow: the customer creates
  // their account and is redirected to Stripe Checkout instead of into
  // a 3-day trial. The plan label maps to the Stripe price catalog
  // ("essentials" → starter, "professional" → growth, "enterprise" →
  // enterprise) — we send the canonical plan_key to the backend.
  const skipTrial = (params.get("skip_trial") || "").toLowerCase() === "true";
  const planKey: "starter" | "growth" | "enterprise" | null = useMemo(() => {
    const p = (plan || "").toLowerCase();
    if (p === "essentials" || p === "starter") return "starter";
    if (p === "professional" || p === "growth") return "growth";
    if (p === "enterprise") return "enterprise";
    return null;
  }, [plan]);
  // Skip-trial requires a recognized plan; if the customer arrives
  // with ``?skip_trial=true`` but no plan key (or an unknown one),
  // we silently fall back to the trial flow rather than 400ing.
  const directSubscribe = skipTrial && planKey !== null;

  if (isAuthenticated) return <Navigate to="/" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    // For Google-flow signup, mint a high-entropy random password
    // server-side-irrelevant — the user authenticates via Google
    // going forward. If they ever want password access they can
    // run a normal "forgot password" flow.
    let effectivePassword = password;
    if (googleSignup) {
      // 32 hex chars + a separator + 32 more = ~256 bits of entropy.
      // ``crypto.getRandomValues`` is widely supported and avoids
      // pulling a dependency just for password generation.
      const buf = new Uint8Array(32);
      crypto.getRandomValues(buf);
      effectivePassword = Array.from(buf)
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
    } else {
      if (password.length < 8) {
        setError("Password must be at least 8 characters.");
        return;
      }
      if (password !== confirm) {
        setError("Passwords don't match.");
        return;
      }
    }
    const { first, last } = splitName(fullName);
    if (!first) {
      setError("Please enter your full name.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await signupOrg({
        org_name: orgName.trim(),
        admin_email: email.trim(),
        password: effectivePassword,
        first_name: first,
        last_name: last,
        company_size: companySize || undefined,
        preferred_slug: slug || undefined,
        // Submitting the form = accepting both policies. The inline
        // disclaimer above the button spells that out, matching the
        // pattern used by most SaaS signups.
        accept_terms: true,
        accept_privacy: true,
        // Skip-trial wires through to the backend, which creates the
        // org without trialing status and returns a Stripe Checkout
        // URL we redirect to below.
        skip_trial: directSubscribe,
        plan_key: directSubscribe ? planKey ?? undefined : undefined,
      });
      setAccessToken(res.access_token);

      if (res.checkout_url) {
        // Direct-subscribe flow: hand off to Stripe's hosted Checkout.
        // The customer's payment + Stripe webhook will flip
        // subscription_status → "active". Their access_token is
        // already set, so when Stripe redirects them back to /
        // post-payment, AuthContext picks them up as a paying user.
        window.location.href = res.checkout_url;
        return;
      }

      // Trial flow: full reload so AuthContext refetches /auth/me cleanly,
      // then the AppShell's onboarding redirect fires and the first-admin
      // wizard opens at /onboarding.
      window.location.assign("/");
    } catch (err: unknown) {
      setError(humanizeSignupError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12 relative">
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="w-full max-w-lg"
      >
        <div className="text-center mb-8">
          <div className="flex justify-center">
            <img src="/logo.png" alt="Lenavio" className="h-10 w-auto" />
          </div>
          <h1 className="text-2xl font-semibold font-heading text-text-primary mt-6">
            {directSubscribe ? "Create your account" : "Start your 3-day trial"}
          </h1>
          <p className="text-text-muted mt-2">
            {directSubscribe ? (
              <>
                You'll be redirected to Stripe to complete payment.
                {plan ? (
                  <>
                    {" "}
                    <span className="text-accent">{plan} plan</span>
                  </>
                ) : null}
              </>
            ) : (
              <>
                3 days free. No credit card required.
                {plan ? (
                  <>
                    {" "}
                    <span className="text-accent">{plan} plan</span>
                  </>
                ) : null}
              </>
            )}
          </p>
        </div>

        <Card className="p-6">
          {/*
            Google sign-up button + divider sit ABOVE the form on the
            normal-signup path, matching the LoginPage layout. When
            the visitor arrives via Google (``?provider=google``), we
            hide both — they're already past the OAuth step and just
            need to fill out company info — and surface a short note
            telling them password fields are gone for a reason.
          */}
          {!googleSignup && (
            <>
              <GoogleAuthButton mode="signup" />
              <OrEmailDivider />
            </>
          )}
          {googleSignup && (
            <div className="mb-4 rounded-lg border border-accent/30 bg-accent/5 px-4 py-3 text-sm text-text-primary">
              <strong>Welcome!</strong> We've got your Google profile —
              just complete your workspace setup below. You'll keep
              signing in with Google going forward, no Lenavio password
              required.
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <Input
              label="Company name"
              required
              autoComplete="organization"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
            />

            <div className="space-y-1">
              <label
                htmlFor="slug"
                className="block text-sm font-medium text-text-primary"
              >
                Workspace URL
              </label>
              <div className="flex items-stretch rounded-lg border border-border bg-surface overflow-hidden focus-within:ring-2 focus-within:ring-accent">
                <span className="px-3 py-2 text-sm text-text-muted bg-background border-r border-border select-none">
                  lenavio.app/
                </span>
                <input
                  id="slug"
                  value={slug}
                  onChange={(e) => {
                    setSlug(slugifyCompany(e.target.value));
                    setSlugDirty(true);
                  }}
                  className="flex-1 px-3 py-2 text-sm bg-transparent outline-none"
                  aria-describedby="slug-hint"
                />
              </div>
              <p id="slug-hint" className="text-xs text-text-muted">
                Lowercase letters, numbers, and hyphens only.
              </p>
            </div>

            <Input
              label="Full name"
              required
              autoComplete="name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />

            <Input
              type="email"
              label="Work email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            {/* Password fields are skipped on the Google flow — the
                user already authenticated via Google, and the form
                handler synthesises a high-entropy random password
                under the hood so the existing /signup/org endpoint
                stays satisfied. They re-authenticate through Google
                next time, or run "forgot password" to set one. */}
            {!googleSignup && (
              <>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Input
                    type="password"
                    label="Password"
                    required
                    minLength={8}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <Input
                    type="password"
                    label="Confirm password"
                    required
                    minLength={8}
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                  />
                </div>
                {/* Live password requirements — uses platform defaults
                    here because the org doesn't exist yet (this IS the
                    org-creation flow). Server enforces the org policy
                    once the row is created. */}
                <PasswordRequirements password={password} />
              </>
            )}

            <div className="space-y-1">
              <label
                htmlFor="company-size"
                className="block text-sm font-medium text-text-primary"
              >
                Company size (optional)
              </label>
              <select
                id="company-size"
                value={companySize}
                onChange={(e) => setCompanySize(e.target.value)}
                className="block w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm"
              >
                {SIZE_OPTIONS.map((o) => (
                  <option key={o.value || "none"} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            {error && (
              <div
                role="alert"
                className="rounded-md bg-danger/10 px-3 py-2 text-sm text-danger"
              >
                {error}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {directSubscribe
                    ? "Redirecting to payment…"
                    : "Creating your workspace…"}
                </>
              ) : directSubscribe ? (
                "Continue to payment"
              ) : (
                "Create workspace"
              )}
            </Button>

            <p className="text-center text-xs text-text-muted">
              By creating an account you agree to our{" "}
              <a
                href={TERMS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent hover:underline"
              >
                Terms
              </a>{" "}
              and{" "}
              <a
                href={PRIVACY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent hover:underline"
              >
                Privacy Policy
              </a>
              .
            </p>

            <p className="text-center text-sm text-text-muted">
              Already have an account?{" "}
              <Link to="/login" className="text-accent hover:underline">
                Sign in
              </Link>
            </p>
          </form>
        </Card>
      </motion.div>
    </div>
  );
}

export default RegisterPage;
